import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SharedService } from '@app/_components/_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class AppEventService {

	constructor(
		private http: HttpClient,
		private sharedService: SharedService
	) { }

	urlEndpoint = 'events';

	findByFilter(
		page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any,
		extraParams: { [key: string]: string | string[] } = {}
	): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		Object.keys(extraParams).forEach(k => {
			const val = extraParams[k];
			if (val == null || val === '') return;
			if (Array.isArray(val)) {
				val.forEach(v => { params = params.append(k, v); });
			} else {
				params = params.set(k, val);
			}
		});
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params }).pipe(retry(1));
	}

	getSummary(dateFrom?: string, dateTo?: string): Observable<ApiResponse> {
		let params = new HttpParams();
		if (dateFrom) params = params.set('dateFrom', dateFrom);
		if (dateTo) params = params.set('dateTo', dateTo);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/events/summary`, { params }).pipe(retry(1));
	}

	getTimeseries(dateFrom?: string, dateTo?: string, eventType?: string, eventCategory?: string): Observable<ApiResponse> {
		let params = new HttpParams();
		if (dateFrom) params = params.set('dateFrom', dateFrom);
		if (dateTo) params = params.set('dateTo', dateTo);
		if (eventType) params = params.set('eventType', eventType);
		if (eventCategory) params = params.set('eventCategory', eventCategory);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/events/timeseries`, { params }).pipe(retry(1));
	}

	getTopUsers(dateFrom?: string, dateTo?: string, eventType?: string, limit: number = 20): Observable<ApiResponse> {
		let params = new HttpParams().set('limit', String(limit));
		if (dateFrom) params = params.set('dateFrom', dateFrom);
		if (dateTo) params = params.set('dateTo', dateTo);
		if (eventType) params = params.set('eventType', eventType);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/events/top-users`, { params }).pipe(retry(1));
	}

	getMetadata(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/events/metadata`).pipe(retry(1));
	}

	logActivity(eventType: string, metadata: object): Observable<any> {
		return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}`, {
			eventType,
			eventCategory: 'BIZDEV',
			metadata: JSON.stringify(metadata),
		});
	}

	exportCsvUrl(filters: { [key: string]: string | string[] } = {}): string {
		const parts: string[] = [];
		Object.keys(filters).forEach(k => {
			const val = filters[k];
			if (val == null || val === '') return;
			if (Array.isArray(val)) {
				val.forEach(v => parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`));
			} else {
				parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(val)}`);
			}
		});
		const qs = parts.join('&');
		return `${environment.apiUrl}/events/export${qs ? '?' + qs : ''}`;
	}
}

export interface AppEvent {
	appEventId: number;
	eventType: string;
	eventCategory: string;
	clickedAt: string;
	userId?: number;
	username?: string;
	visitorId?: string;
	sessionId?: string;
	propertyId?: number;
	trackedLinkId?: number;
	searchQuery?: string;
	resultCount?: number;
	responseTimeMs?: number;
	amount?: number;
	metadata?: string;
	ipHash?: string;
	userAgent?: string;
	referer?: string;
	deviceType?: string;
	country?: string;
	region?: string;
}
